import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { FloppyDisk, Sparkle } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "../../lib/api";

export default function OpportunityPanel() {
  const [cfg, setCfg] = useState(null);
  const [draft, setDraft] = useState(null);
  const [stats, setStats] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, s] = await Promise.all([
        api.get("/admin/insights/addon-opportunity-config"),
        api.get("/admin/insights/opportunity-stats"),
      ]);
      setCfg(c.data);
      setDraft({ ...c.data });
      setStats(s.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/insights/addon-opportunity-config", draft);
      toast.success("Opportunity rules updated.");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  if (!cfg || !draft || !stats) return <div className="text-muted-foreground" data-testid="admin-opp-loading">Loading…</div>;

  const dirty = JSON.stringify(cfg) !== JSON.stringify(draft);

  return (
    <div className="space-y-6" data-testid="admin-opp-panel">
      <div className="card-flat p-6">
        <h3 className="font-serif text-2xl mb-2 inline-flex items-center gap-2">
          <Sparkle size={18} weight="duotone" className="text-[hsl(var(--secondary))]" /> Add-on opportunity rules
        </h3>
        <p className="text-sm text-muted-foreground mb-5">
          When a Basic-tier user views a locked add-on card N times in M days, the dashboard surfaces a one-click discount offer.
        </p>

        <div className="grid sm:grid-cols-4 gap-4 mb-4">
          <Toggle
            label="Enabled"
            value={draft.enabled}
            onChange={(v) => setDraft({ ...draft, enabled: v })}
            testid="admin-opp-enabled"
          />
          <Field label="Threshold views" type="number" min={1} max={50}
            value={draft.threshold_views}
            onChange={(v) => setDraft({ ...draft, threshold_views: parseInt(v || "1") })}
            testid="admin-opp-threshold-views"
          />
          <Field label="Window days" type="number" min={1} max={90}
            value={draft.threshold_days}
            onChange={(v) => setDraft({ ...draft, threshold_days: parseInt(v || "1") })}
            testid="admin-opp-threshold-days"
          />
          <Field label="Discount %" type="number" min={0} max={90}
            value={draft.discount_pct}
            onChange={(v) => setDraft({ ...draft, discount_pct: parseInt(v || "0") })}
            testid="admin-opp-discount"
          />
        </div>
        <button
          onClick={save}
          disabled={saving || !dirty}
          className="btn-primary px-4 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-50"
          data-testid="admin-opp-save"
        >
          <FloppyDisk size={14} weight="duotone" /> {saving ? "Saving…" : "Save rules"}
        </button>
      </div>

      <div className="card-flat p-0 overflow-hidden">
        <div className="px-5 py-3 border-b hairline">
          <h3 className="font-serif text-xl">
            Eligible right now ({stats.candidates.length})
          </h3>
          <p className="text-xs text-muted-foreground">
            Basic users who hit the {stats.config.threshold_views}-view threshold for at least one feature in the last {stats.config.threshold_days} day(s). They will see the discount banner on next dashboard load.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="px-5 py-2">User</th>
                <th className="px-5 py-2">Feature</th>
                <th className="px-5 py-2">Views</th>
              </tr>
            </thead>
            <tbody>
              {stats.candidates.length === 0 && (
                <tr><td colSpan={3} className="px-5 py-8 text-center text-muted-foreground" data-testid="admin-opp-empty">No qualifying users yet.</td></tr>
              )}
              {stats.candidates.map((c, i) => (
                <tr key={`${c.user_id}-${c.feature}-${i}`} className="border-t hairline" data-testid={`admin-opp-row-${i}`}>
                  <td className="px-5 py-2">
                    <div className="font-medium">{c.name || "—"}</div>
                    <div className="text-xs text-muted-foreground">{c.email}</div>
                  </td>
                  <td className="px-5 py-2 font-mono text-xs">{c.feature}</td>
                  <td className="px-5 py-2 num-metric">{c.views}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Toggle({ label, value, onChange, testid }) {
  return (
    <label className="flex items-end gap-2 text-sm">
      <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} data-testid={testid} />
      <span className="text-xs">{label}</span>
    </label>
  );
}

function Field({ label, value, onChange, type = "text", min, max, testid }) {
  return (
    <label className="text-sm">
      <span className="block text-xs text-muted-foreground mb-1">{label}</span>
      <input type={type} min={min} max={max} value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-background border hairline px-3 py-2 text-sm"
        data-testid={testid}
      />
    </label>
  );
}
